<template>
	<div class="page">
		<ErrorEmptyState
			v-if="
				(coresStore.loadError && coresStore.defaultCoresList === undefined) ||
				coresStore.userTierResolutionFailed
			"
			title="Could not load Cores"
			message="Often this is a temporary network or connectivity issue."
			:retrying="retryingLoad"
			@retry="retryFetchCores"
		/>
		<template v-else>
			<div class="content-sections">
				<!-- CUSTOM CORES -->
				<template v-if="userCustomCores.length > 0">
					<section class="stack">
						<div class="section-title section-title--baseline userflow-cores-heading">
							<div class="sgnl-h6-semibold userflow-cores-heading-title">Custom Cores</div>
							<QuotaCount
								:used="userCustomCores.length"
								limit-id="CoreCount"
							/>
							<v-spacer />
							<LimitableActionButton
								@valid-click="addCore"
								variant="flat"
								color="primary"
								width="auto"
								tooltipText="Add Core"
								data-test="add-core-button"
								:limits-to-check="[UserLimitsE.CoreCount]"
								:disabled="tierLimitsUnresolved"
							>
								<template #btnContent>
									<Add16 />
									Add New Core
								</template>
							</LimitableActionButton>
						</div>
						<div class="cores-grid">
							<CoreCard
								v-for="executionCore in userCustomCores"
								:key="executionCore.CoreID"
								:coreConfig="executionCore"
								@edit-core="editCore"
								@delete-core="deleteCore"
								@core-synced="updateCoreList"
							/>
							<LoadingCard
								v-if="coresStore.loadingCores"
								loadingText="Loading Cores"
							/>
						</div>
					</section>
				</template>

				<section class="stack">
					<!-- SIGNALOID DEFAULT CORES -->
					<div class="section-title userflow-cores-heading">
						<div class="sgnl-h6-semibold userflow-cores-heading-title">Signaloid Cores</div>
						<v-spacer />
						<LimitableActionButton
							@valid-click="addCore"
							variant="flat"
							color="primary"
							tooltipText="Add Core"
							data-test="add-core-button"
							:limits-to-check="[UserLimitsE.CoreCount]"
							:disabled="tierLimitsUnresolved"
							v-if="userCustomCores.length === 0"
						>
							<template #btnContent>
								<Add16 />
								Add New Core
							</template>
						</LimitableActionButton>
					</div>
					<div class="cores-grid">
						<CoreCard
							v-for="executionCore in defaultCores"
							:key="executionCore.CoreID"
							:coreConfig="executionCore"
							@edit-core="editCore"
							@delete-core="deleteCore"
							@core-synced="updateCoreList"
						/>
						<LoadingCard
							v-if="coresStore.loadingCores"
							loadingText="Loading Cores"
						/>
					</div>
				</section>
			</div>
		</template>

		<ModifyCoreDialog
			v-if="showModifyCoreDialog"
			:showDialog="showModifyCoreDialog"
			:editCoreData="editCoreData"
			@close-dialog="showModifyCoreDialog = false"
			@core-created="coreCreatedHandler"
			@core-updated="coreUpdatedHandler"
		/>
		<DeleteCoreDialog
			v-if="showDeleteCoreDialog"
			:showDialog="showDeleteCoreDialog"
			:deleteCoreData="deleteCoreData"
			@close-dialog="showDeleteCoreDialog = false"
			@core-deleted="coreSuccessfullyDeleted"
		/>
		<v-snackbar
			v-model="successSnackbar"
			:timeout="4000"
			class="snackbar-success"
		>
			<div class="snackbar-icon-container snackbar-success">
				<CheckmarkFilled16 />
			</div>
			{{ successMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="successSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>
<script>
/* eslint-disable vue/multi-word-component-names */
import { defineComponent } from "vue";

// Components
import CoreCard from "@/components/Cores/CoreCard.vue";
import ModifyCoreDialog from "@/components/Cores/ModifyCoreDialog.vue";
import DeleteCoreDialog from "@/components/Cores/DeleteCoreDialog.vue";
import LimitableActionButton from "@/components/Common/LimitableActionButton.vue";
import LoadingCard from "@/components/LoadingCard.vue";
import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";
import QuotaCount from "@/components/Common/QuotaCount.vue";
import Add16 from "@carbon/icons-vue/es/add/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import Close16 from "@carbon/icons-vue/es/close/16";

// Stores
import { useCoresStore } from "@/stores/cores";
import { useUserStore } from "@/stores/user";
import { useRootStore } from "@/stores/root";
import { mapState } from "pinia";

// Types
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { UserLimitsE } from "@/js/tierconfig";
import { MINIMUM_RETRY_FEEDBACK_MS, withMinimumDuration } from "@/js/utilities";

export default defineComponent({
	name: "Cores",
	components: {
		CoreCard,
		ModifyCoreDialog,
		DeleteCoreDialog,
		LimitableActionButton,
		LoadingCard,
		ErrorEmptyState,
		QuotaCount,
		Add16,
		CheckmarkFilled16,
		Close16,
	},
	data: () => ({
		showModifyCoreDialog: false,
		showDeleteCoreDialog: false,
		editCoreData: undefined,
		deleteCoreData: undefined,
		coresDisabledByUserTier: [],
		successSnackbar: false,
		successMessage: "",
		retryingLoad: false,
	}),
	setup() {
		const coresStore = useCoresStore();
		const userStore = useUserStore();
		const rootStore = useRootStore();
		return { coresStore, userStore, rootStore, UserLimitsE };
	},
	methods: {
		addCore() {
			//@ts-ignore
			this.$posthog?.capture("create_core_button_clicked");

			this.editCoreData = undefined;
			this.showModifyCoreDialog = true;
		},
		async updateCoreList() {
			this.rootStore.tierLimitEventBus.emit({
				type: TierLimitEventTypeE.UsageChanged,
				affectedLimits: [UserLimitsE.CoreCount],
			});
			await this.coresStore.fetchUserCoresFromRemote();
			this.coresDisabledByUserTier = await this.coresStore.getCoresFilteredCoresByUserTier();
		},
		editCore(coreInfo) {
			this.editCoreData = coreInfo;
			this.showModifyCoreDialog = true;
		},
		deleteCore(coreInfo) {
			this.deleteCoreData = coreInfo;
			this.showDeleteCoreDialog = true;
		},
		async coreCreatedHandler() {
			await this.updateCoreList();
			this.successMessage = "Core created successfully.";
			this.successSnackbar = true;
		},
		async coreUpdatedHandler() {
			await this.updateCoreList();
			this.successMessage = "Core updated successfully.";
			this.successSnackbar = true;
		},
		async coreSuccessfullyDeleted() {
			//@ts-ignore
			this.$posthog?.capture("custom_core_deleted");

			await this.updateCoreList();
			this.successMessage = "Core deleted successfully.";
			this.successSnackbar = true;
		},
		async retryFetchCores() {
			this.retryingLoad = true;
			try {
				await withMinimumDuration(
					this.coresStore.initialiseExecutionCoresFromRemote(),
					MINIMUM_RETRY_FEEDBACK_MS,
				);
			} finally {
				this.retryingLoad = false;
			}
		},
	},
	computed: {
		...mapState(useCoresStore, {
			filteredVisibleCoresList: "filteredVisibleCoresList",
		}),
		defaultCores() {
			return this.filteredVisibleCoresList.filter((obj) => {
				return obj.Owner === "*" && obj.Class == "C0Pro";
			});
		},
		userCustomCores() {
			return this.filteredVisibleCoresList.filter((obj) => {
				return obj.Owner !== "*";
			});
		},
		tierLimitsUnresolved() {
			return this.userStore.currentUserTierDetails === undefined;
		},
	},
	created() {
		this.coresStore.getCoresFilteredCoresByUserTier();
	},
});
</script>

<style lang="scss" scoped>
@media (max-width: 599px) {
	.section-title {
		flex-direction: column;
		align-items: stretch;
		gap: 0.75rem;
	}

	.section-title > .v-spacer {
		display: none;
	}

	.section-title > span {
		display: block;
	}
}

.cores-grid {
	display: grid;
	gap: var(--sgnl-space-16);
	grid-template-columns: 1fr;
}

@media (min-width: map-get($grid-breakpoints, md)) {
	.cores-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}
</style>
